import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Radio, Table } from "antd";
import { useGetSeguimiento } from "./hooks";
import { setFiltrosVisorSeguimiento } from "../../../context/actions/semaforoActions";
import { dateFromString } from "../../../utils/dateFromString";
import { SemaforoVisor } from "../shared/SemaforoVisor";
import { searchFilterDropdown } from "../shared/columnFilters";
import { DetalleServicio } from "./DetalleServicio";
import CardPage from "../../../layout/CardPage";

const MINUTOS_SIN_CONTACTO = 20;
const MINUTOS_TAB_VENCIDOS = 15;

const diffMinutes = (date) => {
  if (!date) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60000));
};

const getUltimoContacto = (ultimoContacto) => {
  if (ultimoContacto) {
    const minutos = diffMinutes(ultimoContacto);
    return {
      texto: `Hace ${minutos} ${minutos === 1 ? "minuto" : "minutos"}`,
      vencido: minutos > MINUTOS_SIN_CONTACTO,
    };
  }

  return { texto: "", vencido: false };
};

const estaVencidoPorMinutos = ({ ultimoContacto }, minutosLimite) => {
  if (ultimoContacto) return diffMinutes(ultimoContacto) > minutosLimite;
  return false;
};

const AlertaContacto = () => (
  <span
    className="blink_text"
    style={{
      display: "inline-block",
      width: 9,
      height: 9,
      marginRight: 6,
      borderRadius: "50%",
      backgroundColor: "#ca2228",
    }}
  />
);

export const SemaforoSeguimiento = () => {
  const { servicios, loading, error } = useGetSeguimiento();
  const [tab, setTab] = useState("todos");
  const [pageSizeVencidos, setPageSizeVencidos] = useState(5);
  const dispatch = useDispatch();
  const { filtrosVisorSeguimiento } = useSelector((state) => state.semaforoReducer);
  const updateFiltro = (f) => dispatch(setFiltrosVisorSeguimiento(f));
  const onPanelChange = (keys) => updateFiltro({ openKeys: keys });
  const vencidos = servicios.filter((servicio) => estaVencidoPorMinutos(servicio, MINUTOS_TAB_VENCIDOS));

  const columns = [
    {
      title: "Asistencia ID",
      dataIndex: "asistenciaID",
      key: "asistenciaID",
      width: 150,
      render: (id) => <a href={`/detalleAsistencia/${id}`} target="_blank" rel="noopener noreferrer">{id}</a>,
      filterDropdown: searchFilterDropdown("Buscar ID", "searchId", updateFiltro),
      onFilter: (v, r) => r.asistenciaID.toLowerCase().includes(v.toLowerCase()),
    },
    {
      title: "Proveedor",
      dataIndex: "proveedor",
      key: "proveedor",
      width: 200,
      filterDropdown: searchFilterDropdown("Buscar proveedor", "searchProveedor", updateFiltro),
      onFilter: (v, r) => r.proveedor?.toLowerCase().includes(v.toLowerCase()),
    },
    {
      title: "Servicio",
      dataIndex: "servicio",
      key: "servicio",
      width: 160,
    },
    {
      title: "Fecha Asignación",
      dataIndex: "fechaAsignacion",
      key: "fechaAsignacion",
      width: 180,
      render: (f) => (f ? dateFromString(f).dateStep : ""),
      sorter: (a, b) => new Date(a.fechaAsignacion) - new Date(b.fechaAsignacion),
    },
    {
      title: "Llegada Estimada",
      dataIndex: "fechaTiempoLlegada",
      key: "fechaTiempoLlegada",
      width: 180,
      render: (f) => (f ? dateFromString(f).dateStep : ""),
      sorter: (a, b) => new Date(a.fechaTiempoLlegada) - new Date(b.fechaTiempoLlegada),
    },
    {
      title: "Último Contacto",
      dataIndex: "ultimoContacto",
      key: "ultimoContacto",
      width: 170,
      render: (ultimoContacto, record) => {
        const { texto, vencido } = getUltimoContacto(ultimoContacto);
        return (
          <span>
            {vencido && <AlertaContacto />}
            {texto}
          </span>
        );
      },
      sorter: (a, b) => {
        const fechaA = a.ultimoContacto || a.fechaAsignacion;
        const fechaB = b.ultimoContacto || b.fechaAsignacion;
        return new Date(fechaA) - new Date(fechaB);
      },
    },
    {
      title: "Ubicación",
      dataIndex: "ubicacion",
      key: "ubicacion",
      width: 200,
      filterDropdown: searchFilterDropdown("Buscar ubicación", "searchUbicacion", updateFiltro),
      onFilter: (v, r) => r.ubicacion?.toLowerCase().includes(v.toLowerCase()),
    },
  ];

  const columnsVencidos = [
    ...columns,
    {
      title: "Usuario",
      dataIndex: "usuarioIniciaSeguimiento",
      key: "usuarioIniciaSeguimiento",
      width: 180,
      render: (usuario) => usuario || "Sin seguimiento",
      filterDropdown: searchFilterDropdown("Buscar usuario", "searchUsuarioSeguimiento", updateFiltro),
      onFilter: (v, r) => (r.usuarioIniciaSeguimiento || "Sin seguimiento").toLowerCase().includes(v.toLowerCase()),
    },
  ];

  const titulo = (
    <div>
      <div>Semáforo Seguimiento</div>
      <Radio.Group
        value={tab}
        onChange={(e) => setTab(e.target.value)}
        size="small"
        optionType="button"
        buttonStyle="solid"
        style={{ marginTop: 8 }}
      >
        <Radio.Button value="todos">Todos</Radio.Button>
        <Radio.Button value="vencidos">Vencidos</Radio.Button>
      </Radio.Group>
    </div>
  );

  if (error) return <Alert type="error" message="No se pudo consultar el semáforo de seguimiento" />;

  if (tab === "vencidos") {
    return (
      <CardPage titulo={titulo}>
        <Table
          columns={columnsVencidos}
          dataSource={vencidos}
          rowKey="asistenciaID"
          loading={loading}
          pagination={{
            position: ["bottomCenter"],
            pageSize: pageSizeVencidos,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onShowSizeChange: (_, size) => setPageSizeVencidos(size),
          }}
          size="small"
        />
      </CardPage>
    );
  }

  return (
    <SemaforoVisor
      titulo={titulo}
      columns={columns}
      data={servicios}
      loading={loading}
      groupBy="usuarioIniciaSeguimiento"
      defaultGroupLabel="Sin seguimiento"
      expandedRowRender={(record) => <DetalleServicio servicio={record} />}
      activeKey={filtrosVisorSeguimiento.openKeys}
      onPanelChange={onPanelChange}
    />
  );
};
